import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as actions from '@redux-actions/';

import { base64FileToImage, base64IsImage, formatPhone, getLabelFallback } from '@utils/utils';
import ArrowRight from '@images/svg-icons/arrowRight.svg';
import { IMySavedPrescriptionItem } from '@interfaces/contactsInterfaces';
import LastUpdate from './LastUpdate';
import Modal from '@components/styled-UI/Modal';
import PrescriptionDefaultImage from '@images/svg-icons/prescriptionDefaultImage.svg';
import { downloadPrescriptionFile } from '@services/contactsServices';
import { RootState } from 'src/store/store';
import clsx from 'clsx';

interface Props {
    prescription: IMySavedPrescriptionItem;
}

const SavedPrescriptionCard = ({ prescription }: Props) => {
    const config = useSelector((state: RootState) => state.common.config);
    const dispatch = useDispatch();

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [prescriptionFile, setPrescriptionFile] = useState(null);
    const [prescriptionImage, setPrescriptionImage] = useState(null);

    const prescriptionFileSize = !!prescriptionFile
        ? ((4 * Math.ceil(prescriptionFile?.length / 3) * 0.5624896334383812) / 1000000).toFixed(2)
        : '';

    useEffect(() => {
        const controller = new AbortController();
        if (!!prescription?.fileName) {
            downloadPrescriptionFile(
                config.mockRequest,
                config.storeId.toString(),
                prescription.fileName,
                controller
            ).then(response => {
                if (!!response?.prescriptionFile) {
                    setPrescriptionFile(response?.prescriptionFile);

                    if (base64IsImage(response.prescriptionFile)) {
                        setPrescriptionImage(base64FileToImage(response.prescriptionFile));
                    }
                }
            });
        }

        return () => controller.abort();
    }, []);

    return (
        <div className="saved-prescription-card">
            <div className="saved-prescription-card-content">
                <div role="region" aria-label="card title" className="card-title">
                    Prescription name: {prescription.prescriptionName}
                    <button
                        className="card-arrow"
                        onClick={() => dispatch(actions.setCurrentPrescription(prescription))}
                        data-analytics_available_call="0"
                    >
                        <ArrowRight />
                    </button>
                </div>

                <div className="presc-upload-date-container">
                    <LastUpdate prescription={prescription} />
                </div>

                <button
                    className="show-presc-btn-content"
                    data-element-id="Prod_MyPrescriptions-View"
                    onClick={() => setIsPreviewModalOpen(true)}
                    data-analytics_available_call="0"
                >
                    {getLabelFallback('CONTACT_LENSES_SHOW_SAVED_PRESCRIPTION')}
                </button>
            </div>

            <Modal isOpen={isPreviewModalOpen} close={() => setIsPreviewModalOpen(false)}>
                <div className="prescription-preview-modal">
                    <div className="prescription-preview-modal_title">Your prescription</div>

                    <p className="prescription-preview-modal_subtitle">
                        We’ll use this information to make sure everything in your order is correct.
                        We’ve got you covered.
                    </p>

                    <div className="prescription-preview-modal_section">
                        <div className="prescription-preview-modal_section_title">
                            Your doctor&apos;s details
                        </div>

                        <div
                            className={clsx('doctor-information-table', {
                                'retail-table-info': prescription.isPrescriptionRetail,
                            })}
                        >
                            {!prescription.isPrescriptionRetail && (
                                <>
                                    <div className="doctor-information-table_header-item">
                                        Doctor name
                                    </div>
                                    <div className="doctor-information-table_body-item">
                                        {prescription.doctor.name}
                                    </div>
                                    <div className="doctor-information-table_header-item">
                                        Clinic name
                                    </div>
                                    <div className="doctor-information-table_body-item">
                                        {prescription.doctor.name}
                                    </div>
                                </>
                            )}
                            {prescription.isPrescriptionRetail && (
                                <>
                                    <div className="doctor-information-table_header-item">
                                        Store name
                                    </div>
                                    <div className="doctor-information-table_body-item">
                                        {prescription.doctor.name.trim().charAt(0).toUpperCase() +
                                            prescription.doctor.name.trim().slice(1)}
                                    </div>
                                </>
                            )}

                            <div className="doctor-information-table_header-item">Address</div>
                            <div className="doctor-information-table_body-item">
                                {!prescription.isPrescriptionRetail && prescription.doctor.address}
                                {prescription.isPrescriptionRetail &&
                                    !!prescription.doctor.address &&
                                    prescription.doctor.address.split(',')[0]}
                                {prescription.isPrescriptionRetail &&
                                    !!prescription.doctor.address && <br />}
                                {prescription.isPrescriptionRetail &&
                                    !!prescription.doctor.address &&
                                    prescription.doctor.address.split(',')[1] +
                                        ', ' +
                                        prescription.doctor.address.split(',')[2] +
                                        ' ' +
                                        prescription.doctor.address.split(',')[3]}
                                {!!prescription.doctor.address && !!prescription.doctor.phone && (
                                    <br />
                                )}
                                {!!prescription.doctor.phone &&
                                    formatPhone(prescription.doctor.phone)}
                            </div>
                        </div>
                    </div>

                    {prescription.submissionMethod === 'uploadPrescription' && (
                        <div className="prescription-preview-modal_section">
                            <div className="prescription-preview-modal_section_title">
                                Your submission method
                            </div>

                            <div className="prescription-file">
                                <div className="prescription-file_image">
                                    {!!prescriptionImage ? (
                                        <img src={prescriptionImage} />
                                    ) : (
                                        <PrescriptionDefaultImage />
                                    )}
                                </div>

                                <div className="prescription-file_data">
                                    {prescription?.fileName} - [{prescriptionFileSize} MB]
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default SavedPrescriptionCard;
